"use strict";
var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
const differenceInMinutes_1 = __importDefault(require("date-fns/differenceInMinutes"));
const format_1 = __importDefault(require("date-fns/format"));
const find_1 = __importDefault(require("lodash/find"));
const get_1 = __importDefault(require("lodash/get"));
const isEmpty_1 = __importDefault(require("lodash/isEmpty"));
const map_1 = __importDefault(require("lodash/map"));
const mobx_1 = require("mobx");
const getQuoteDetails_1 = __importDefault(require("../clients/fleetquote/getQuoteDetails"));
const API_STATE_1 = __importDefault(require("../constants/app/API_STATE"));
const UNLIMITED_KM_1 = __importDefault(require("../constants/app/UNLIMITED_KM"));
const addOnFactory_1 = __importDefault(require("../factories/addon/addOnFactory"));
const branchFactory_1 = __importDefault(require("../factories/branch/branchFactory"));
const fleetGroupFactory_1 = __importDefault(require("../factories/fleetgroup/fleetGroupFactory"));
const discountFactory_1 = __importDefault(require("../factories/fleetquote/discountFactory"));
const fleetQuoteFactory_1 = __importDefault(require("../factories/fleetquote/fleetQuoteFactory"));
const insuranceFactory_1 = __importDefault(require("../factories/insurance/insuranceFactory"));
const formatPriceValue_1 = __importDefault(require("../factories/utils/common/formatPriceValue"));
const getLocalTimeStampFromGMT_1 = __importDefault(require("../utils/store/getLocalTimeStampFromGMT"));
const mapInitialStateToStoreState_1 = __importDefault(require("../utils/store/mapInitialStateToStoreState"));
/**
 * Fleet Quote Details Domain Store
 */
class FleetQuoteDetailsStore {
    constructor(initialState, $env) {
        this.data = {};
        this.apiState = API_STATE_1.default.INITIAL;
        this.errorData = {};
        /**
         * Fetch Quote Details data from backend
         * @param {string} quoteId
         * @param {function} errorHandler
         */
        this.fetch = (0, mobx_1.flow)(function* (quoteId, errorHandler) {
            try {
                this.apiState = API_STATE_1.default.LOADING;
                this.data = {};
                this.errorData = {};
                const response = yield (0, getQuoteDetails_1.default)(quoteId);
                // 200 http codes
                this.onSuccess(response);
            }
            catch (err) {
                // all other http codes
                this.onError(err, errorHandler);
            }
        });
        /**
         * on success callback
         * @param data
         */
        this.onSuccess = (data) => {
            this.data = data;
            this.apiState = API_STATE_1.default.SUCCESS;
        };
        /**
         * on error callback
         * @param err
         * @param errorHandler
         */
        this.onError = (err, errorHandler) => {
            this.apiState = API_STATE_1.default.FAILED;
            // when a request is cancelled, Axios promise is rejected
            if (err.response) {
                const errResponse = err.response;
                const { data, status } = errResponse;
                // stores axios error info in observable
                this.errorData = {
                    data,
                    status,
                };
                if (errorHandler) {
                    errorHandler(errResponse);
                }
            }
        };
        /**
         * Map initial state to store state
         * @param initialState
         */
        this.updateInitialState = (initialState) => {
            (0, mapInitialStateToStoreState_1.default)(this, initialState);
        };
        this.$env = $env;
        this.updateInitialState(initialState);
    }
    /**
     * Returns true if API state is loading
     * @returns {boolean}
     */
    get isLoading() {
        return this.apiState === API_STATE_1.default.LOADING;
    }
    /**
     * Returns true if API fetch was success
     * @returns {boolean}
     */
    get isSuccess() {
        return this.apiState === API_STATE_1.default.SUCCESS;
    }
    /**
     * Returns true if API fetch has failed
     * @returns {boolean}
     */
    get hasFailed() {
        return this.apiState === API_STATE_1.default.FAILED;
    }
    /**
     * Returns true if API fetch returned no data
     * @returns {boolean}
     */
    get hasNoData() {
        return this.isSuccess && (0, isEmpty_1.default)(this.data);
    }
    /**
     * Returns current config language
     * @returns {LANG_VALUES}
     */
    get language() {
        return this.$env.config.locale;
    }
    /**
     * Returns quoteId
     * @returns {string}
     */
    get quoteId() {
        return (0, get_1.default)(this.data, 'quoteId', '');
    }
    /**
     * Returns quote expiry
     * @returns {Date} Epoch time stamp eg. JS Time/1000
     */
    get expiry() {
        return (0, get_1.default)(this.data, 'expiry');
    }
    /**
     * Returns the expiry time in mins as compared to now
     * @returns {number}
     */
    get expiryMins() {
        // this.expiry will always be in future
        return (0, differenceInMinutes_1.default)(new Date(this.expiry * 1000), Date.now());
    }
    /**
     * Returns pick up date + time in epoch format
     * BE returns in GMT format
     * @returns {Date}
     */
    get pickupDateTime() {
        return (0, get_1.default)(this.data, 'pickupDateTime', Date.now() / 1000);
    }
    /**
     * Returns formatted date eg. 12 June
     * Converts GMT date to local KSA date
     * @returns {string}
     */
    get pickupDate() {
        const localTimeStamp = (0, getLocalTimeStampFromGMT_1.default)(this.pickupDateTime);
        return (0, format_1.default)(localTimeStamp, 'dd MMMM');
    }
    /**
     * Returns formatted time eg. 10:30 AM
     * Converts GMT time to local KSA time
     * @returns {string}
     */
    get pickupTime() {
        const localTimeStamp = (0, getLocalTimeStampFromGMT_1.default)(this.pickupDateTime);
        return (0, format_1.default)(localTimeStamp, 'hh:mm a');
    }
    /**
     * Returns pick up branch id
     * @returns {number}
     */
    get pickupBranchId() {
        return (0, get_1.default)(this.data, 'pickupBranchId');
    }
    /**
     * Returns formatted pickup branch details
     * @returns {BranchFactoryType}
     */
    get pickupBranch() {
        const rawPickupBranch = (0, get_1.default)(this.data, 'pickupBranch', {});
        return (0, branchFactory_1.default)(rawPickupBranch, this.language);
    }
    /**
     * Returns drop off date + time in epoch format
     * BE returns in GMT format
     * @returns {Date}
     */
    get dropOffDateTime() {
        return (0, get_1.default)(this.data, 'dropOffDateTime', Date.now() / 1000);
    }
    /**
     * Returns formatted date eg. 12 June
     * Converts GMT date to local KSA date
     * @returns {string}
     */
    get dropOffDate() {
        const localTimeStamp = (0, getLocalTimeStampFromGMT_1.default)(this.dropOffDateTime);
        return (0, format_1.default)(localTimeStamp, 'dd MMMM');
    }
    /**
     * Returns formatted time eg. 10:30 AM
     * Converts GMT time to local KSA time
     * @returns {string}
     */
    get dropOffTime() {
        const localTimeStamp = (0, getLocalTimeStampFromGMT_1.default)(this.dropOffDateTime);
        return (0, format_1.default)(localTimeStamp, 'hh:mm a');
    }
    /**
     * Returns drop off branch id
     * @returns {number}
     */
    get dropOffBranchId() {
        return (0, get_1.default)(this.data, 'dropOffBranchId');
    }
    /**
     * Returns formatted dropoff branch details
     * @returns {BranchFactoryType}
     */
    get dropOffBranch() {
        const rawDropoffBranch = (0, get_1.default)(this.data, 'dropOffBranch', {});
        return (0, branchFactory_1.default)(rawDropoffBranch, this.language);
    }
    /**
     * Returns formatted selected insurance list
     * @return {Array}
     */
    get selectedInsuranceList() {
        const rawInsuranceList = (0, get_1.default)(this.data, 'insurances', []);
        return (0, map_1.default)(rawInsuranceList, (insurance) => (0, insuranceFactory_1.default)(insurance, this.language));
    }
    /**
     * Returns formatted selected addons
     * @returns {Array}
     */
    get selectedAddOnsList() {
        const rawAddOnsList = (0, get_1.default)(this.data, 'addons', []);
        return (0, map_1.default)(rawAddOnsList, (addOn) => (0, addOnFactory_1.default)(addOn, this.language));
    }
    /**
     * Returns selected add ons
     * @returns {Array}
     */
    get selectedAddOns() {
        return (0, map_1.default)(this.selectedAddOnsList, (addOn) => String((0, get_1.default)(addOn, 'id')));
    }
    /**
     * Returns true if has Unlimited Km.
     * @returns {Boolean}
     */
    get hasUnlimitedKm() {
        return !!(0, find_1.default)(this.selectedAddOnsList, (addOn) => {
            const addOnCode = (0, get_1.default)(addOn, 'code');
            return addOnCode === UNLIMITED_KM_1.default;
        });
    }
    /**
     * Returns vehicle make, model and group details
     * @returns {FleetGroupFactoryType}
     */
    get vehicleDetails() {
        return (0, fleetGroupFactory_1.default)((0, get_1.default)(this.data, 'vehicleGroup', {}), this.language);
    }
    /**
     * Returns necessary formatted original price details for showing in UI
     * Does not include online or all discount
     * Will be used in Payment screen
     * @returns {object}
     */
    get originalPriceDetails() {
        // get applied promo code
        const promoCode = (0, get_1.default)(this.data, `priceDetails.promotionDetails.code`, '');
        // extract necessary data from fleet quote factory
        const returnOriginalPriceDetails = true;
        const { cdw, finalPrice, currency, soldDays, soldHours, vat, vatPercentage, rental, extra, discount, hasDiscount, dropOffSum, hasDropOffSum, dropOffCharge, dailyKmsAllowance, extraKmsCharge, allowanceKms, isCorporate, } = this.isSuccess &&
            (0, fleetQuoteFactory_1.default)(this.data, returnOriginalPriceDetails);
        return {
            cdw,
            totalPrice: finalPrice,
            currency,
            soldDays,
            soldHours,
            vat,
            vatPercentage,
            rental,
            extra,
            discount,
            hasDiscount,
            promoCode,
            dropOffSum,
            hasDropOffSum,
            dropOffCharge,
            dailyKmsAllowance,
            extraKmsCharge,
            allowanceKms,
            isCorporate,
        };
    }
    /**
     * Returns necessary formatted price details for showing in UI
     * @returns {object}
     */
    get priceDetails() {
        // get applied promo code
        const promoCode = (0, get_1.default)(this.data, `priceDetails.promotionDetails.code`, '');
        // extract necessary data from fleet quote factory
        const { cdw, finalPrice, currency, soldDays, soldHours, vat, vatPercentage, rental, extra, discount, hasDiscount, dropOffSum, hasDropOffSum, dropOffCharge, dailyKmsAllowance, extraKmsCharge, allowanceKms, isCorporate, } = this.isSuccess && (0, fleetQuoteFactory_1.default)(this.data);
        return {
            cdw,
            totalPrice: finalPrice,
            currency,
            soldDays,
            soldHours,
            vat,
            vatPercentage,
            rental,
            extra,
            discount,
            hasDiscount,
            promoCode,
            dropOffSum,
            hasDropOffSum,
            dropOffCharge,
            dailyKmsAllowance,
            extraKmsCharge,
            allowanceKms,
            isCorporate,
        };
    }
    /**
     * Returns true if a promo code applied has all discounts
     * promo code can be auto applied or manually applied
     */
    get hasAllDiscount() {
        return (this.isSuccess &&
            !(0, isEmpty_1.default)((0, get_1.default)(this.data, `priceDetails.allDiscountDetails`, {})));
    }
    /**
     * Returns true if a promo code applied has online discounts
     * promo code can be auto applied or manually applied
     */
    get hasOnlineDiscount() {
        return (this.isSuccess &&
            !(0, isEmpty_1.default)((0, get_1.default)(this.data, `priceDetails.onlineDiscountDetails`, {})));
    }
    /**
     * Returns formatted all discounts data
     * @returns {DiscountFactoryType}
     */
    get allDiscountData() {
        const currency = (0, get_1.default)(this.priceDetails, 'currency', '');
        return this.hasAllDiscount
            ? (0, discountFactory_1.default)((0, get_1.default)(this.data, `priceDetails.allDiscountDetails`, {}), currency)
            : {};
    }
    /**
     * Returns formatted online discounts data
     * @returns {DiscountFactoryType}
     */
    get onlineDiscountData() {
        const currency = (0, get_1.default)(this.priceDetails, 'currency', '');
        return this.hasOnlineDiscount
            ? (0, discountFactory_1.default)((0, get_1.default)(this.data, `priceDetails.onlineDiscountDetails`, {}), currency)
            : {};
    }
    /**
     * Returns online total price
     * Pay Now
     * @returns {string}
     */
    get onlineTotalPrice() {
        let totalPrice = '';
        const currency = (0, get_1.default)(this.priceDetails, 'currency', '');
        if (this.hasOnlineDiscount) {
            totalPrice = (0, get_1.default)(this.onlineDiscountData, 'finalPrice', '');
        }
        else if (this.hasAllDiscount) {
            totalPrice = (0, get_1.default)(this.allDiscountData, 'finalPrice', '');
        }
        else {
            totalPrice = (0, formatPriceValue_1.default)((0, get_1.default)(this.data, 'finalPrice', ''), currency);
        }
        return totalPrice;
    }
    /**
     * Returns offline total price
     * Pay At Branch
     * @returns {string}
     */
    get offlineTotalPrice() {
        let totalPrice = '';
        const currency = (0, get_1.default)(this.priceDetails, 'currency', '');
        if (this.hasAllDiscount) {
            totalPrice = (0, get_1.default)(this.allDiscountData, 'finalPrice', '');
        }
        else {
            totalPrice = (0, formatPriceValue_1.default)((0, get_1.default)(this.data, 'finalPrice', ''), currency);
        }
        return totalPrice;
    }
    /**
     * Returns the price difference between online & offline total prices
     * @returns {number}
     */
    get onlineOfflineTotalPriceDiff() {
        const onlinePrice = Number(this.onlineTotalPrice.split(' ')[0]);
        const offlinePrice = Number(this.offlineTotalPrice.split(' ')[0]);
        const diff = offlinePrice - onlinePrice;
        return Number(diff.toFixed(2));
    }
    /**
     * Returns the formatted onlineOfflineTotalPriceDiff with currency
     * @returns {string}
     */
    get formattedPriceDiff() {
        const { currency } = this.priceDetails;
        return (0, formatPriceValue_1.default)(this.onlineOfflineTotalPriceDiff, currency);
    }
    /**
     * Returns true if creditCardRequired is true
     * @returns {boolean}
     */
    get isCreditCardRequired() {
        return (0, get_1.default)(this.data, 'creditCardRequired', false);
    }
    /**
     * Returns rewardOptions
     * @returns {Array}
     */
    get rewardsOptions() {
        const formattedData = (0, fleetQuoteFactory_1.default)(this.data);
        return (0, get_1.default)(formattedData, 'rewardsOptions', []);
    }
    /**
     * Returns online rewardOptions
     * @returns {Array}
     */
    get onlineRewardOptions() {
        return (0, get_1.default)(this.data, 'priceDetails.onlineDiscountDetails.rewardOptions', []);
    }
    /**
     * Returns allDiscount rewardOptions
     * @returns {Array}
     */
    get allDiscountRewardOptions() {
        return (0, get_1.default)(this.data, 'priceDetails.allDiscountDetails.rewardOptions', []);
    }
    /**
     * Returns value for offlineRewardPoints
     * @returns {number}
     */
    get offlineRewardPoints() {
        if (this.hasAllDiscount) {
            return this.allDiscountRewardOptions;
        }
        return (0, get_1.default)(this.data, 'rewardOptions', []);
    }
}
__decorate([
    mobx_1.observable
], FleetQuoteDetailsStore.prototype, "data", void 0);
__decorate([
    mobx_1.observable
], FleetQuoteDetailsStore.prototype, "apiState", void 0);
__decorate([
    mobx_1.observable
], FleetQuoteDetailsStore.prototype, "errorData", void 0);
__decorate([
    mobx_1.computed
], FleetQuoteDetailsStore.prototype, "isLoading", null);
__decorate([
    mobx_1.computed
], FleetQuoteDetailsStore.prototype, "isSuccess", null);
__decorate([
    mobx_1.computed
], FleetQuoteDetailsStore.prototype, "hasFailed", null);
__decorate([
    mobx_1.computed
], FleetQuoteDetailsStore.prototype, "hasNoData", null);
__decorate([
    mobx_1.computed
], FleetQuoteDetailsStore.prototype, "language", null);
__decorate([
    mobx_1.computed
], FleetQuoteDetailsStore.prototype, "quoteId", null);
__decorate([
    mobx_1.computed
], FleetQuoteDetailsStore.prototype, "expiry", null);
__decorate([
    mobx_1.computed
], FleetQuoteDetailsStore.prototype, "expiryMins", null);
__decorate([
    mobx_1.computed
], FleetQuoteDetailsStore.prototype, "pickupDateTime", null);
__decorate([
    mobx_1.computed
], FleetQuoteDetailsStore.prototype, "pickupDate", null);
__decorate([
    mobx_1.computed
], FleetQuoteDetailsStore.prototype, "pickupTime", null);
__decorate([
    mobx_1.computed
], FleetQuoteDetailsStore.prototype, "pickupBranchId", null);
__decorate([
    mobx_1.computed
], FleetQuoteDetailsStore.prototype, "pickupBranch", null);
__decorate([
    mobx_1.computed
], FleetQuoteDetailsStore.prototype, "dropOffDateTime", null);
__decorate([
    mobx_1.computed
], FleetQuoteDetailsStore.prototype, "dropOffDate", null);
__decorate([
    mobx_1.computed
], FleetQuoteDetailsStore.prototype, "dropOffTime", null);
__decorate([
    mobx_1.computed
], FleetQuoteDetailsStore.prototype, "dropOffBranchId", null);
__decorate([
    mobx_1.computed
], FleetQuoteDetailsStore.prototype, "dropOffBranch", null);
__decorate([
    mobx_1.computed
], FleetQuoteDetailsStore.prototype, "selectedInsuranceList", null);
__decorate([
    mobx_1.computed
], FleetQuoteDetailsStore.prototype, "selectedAddOnsList", null);
__decorate([
    mobx_1.computed
], FleetQuoteDetailsStore.prototype, "selectedAddOns", null);
__decorate([
    mobx_1.computed
], FleetQuoteDetailsStore.prototype, "hasUnlimitedKm", null);
__decorate([
    mobx_1.computed
], FleetQuoteDetailsStore.prototype, "vehicleDetails", null);
__decorate([
    mobx_1.computed
], FleetQuoteDetailsStore.prototype, "originalPriceDetails", null);
__decorate([
    mobx_1.computed
], FleetQuoteDetailsStore.prototype, "priceDetails", null);
__decorate([
    mobx_1.computed
], FleetQuoteDetailsStore.prototype, "hasAllDiscount", null);
__decorate([
    mobx_1.computed
], FleetQuoteDetailsStore.prototype, "hasOnlineDiscount", null);
__decorate([
    mobx_1.computed
], FleetQuoteDetailsStore.prototype, "allDiscountData", null);
__decorate([
    mobx_1.computed
], FleetQuoteDetailsStore.prototype, "onlineDiscountData", null);
__decorate([
    mobx_1.computed
], FleetQuoteDetailsStore.prototype, "onlineTotalPrice", null);
__decorate([
    mobx_1.computed
], FleetQuoteDetailsStore.prototype, "offlineTotalPrice", null);
__decorate([
    mobx_1.computed
], FleetQuoteDetailsStore.prototype, "onlineOfflineTotalPriceDiff", null);
__decorate([
    mobx_1.computed
], FleetQuoteDetailsStore.prototype, "formattedPriceDiff", null);
__decorate([
    mobx_1.computed
], FleetQuoteDetailsStore.prototype, "isCreditCardRequired", null);
__decorate([
    mobx_1.computed
], FleetQuoteDetailsStore.prototype, "rewardsOptions", null);
__decorate([
    mobx_1.computed
], FleetQuoteDetailsStore.prototype, "onlineRewardOptions", null);
__decorate([
    mobx_1.computed
], FleetQuoteDetailsStore.prototype, "allDiscountRewardOptions", null);
__decorate([
    mobx_1.computed
], FleetQuoteDetailsStore.prototype, "offlineRewardPoints", null);
__decorate([
    mobx_1.action.bound
], FleetQuoteDetailsStore.prototype, "onSuccess", void 0);
__decorate([
    mobx_1.action.bound
], FleetQuoteDetailsStore.prototype, "onError", void 0);
__decorate([
    mobx_1.action.bound
], FleetQuoteDetailsStore.prototype, "updateInitialState", void 0);
exports.default = FleetQuoteDetailsStore;
